import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  decideOrder,
  fetchAllOrders,
  fetchProgramMaterial,
  orderAdminAction,
  setOrderLineStatuses,
  setOrderLineDecisions
} from 'api/procurement'
import constants from 'app/constants'
import { confirm, eventAlert, notify } from 'app/helpers'

import AttachmentUploader from '../components/AttachmentUploader'
import ExportButtons from '../components/ExportButtons'
import { downloadProgramMaterialPos } from '../components/poPdf'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { marginBottom: theme.spacing(2) }
}))

const STATUS_COLOR = {
  submitted: { background: '#fff8e1', color: '#8d6e00' },
  approved: { background: '#e8f5e9', color: '#2e7d32' },
  rejected: { background: '#ffebee', color: '#c62828' },
  sent: { background: '#ede7f6', color: '#5e35b1' },
  received: { background: '#e3f2fd', color: '#1565c0' },
  fulfilled: { background: '#e3f2fd', color: '#1565c0' }
}

// "sent" reads as "Sent to centre" so the order's journey is clear at a glance.
const STATUS_LABEL = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  sent: 'Sent to centre',
  received: 'Received',
  fulfilled: 'Fulfilled'
}

const LINE_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'unavailable', label: 'Not available' }
]

const LINE_DECISIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accept' },
  { value: 'rejected', label: 'Reject' }
]

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const numv = (v) => {
  const n = Number(v)
  return Number.isNaN(n) ? 0 : n
}

// Diff the frozen original snapshot against the current lines (match on
// reference_id when present, else item name). Returns rows tagged as
// added / removed / changed / same.
const diffLines = (original, current) => {
  const keyOf = (l) =>
    (l.reference_id && String(l.reference_id)) ||
    String(l.item_name || l.name || '').trim().toLowerCase()
  const origMap = new Map((original || []).map((l) => [keyOf(l), l]))
  const curMap = new Map((current || []).map((l) => [keyOf(l), l]))
  const rows = []
  origMap.forEach((o, k) => {
    const c = curMap.get(k)
    if (!c) {
      rows.push({ key: k, kind: 'removed', name: o.item_name || o.name, orig: o, cur: null })
    } else {
      const changed =
        numv(o.qty) !== numv(c.qty) || numv(o.unit_cost) !== numv(c.unit_cost)
      rows.push({
        key: k,
        kind: changed ? 'changed' : 'same',
        name: c.item_name || c.name,
        orig: o,
        cur: c
      })
    }
  })
  curMap.forEach((c, k) => {
    if (!origMap.has(k)) {
      rows.push({ key: k, kind: 'added', name: c.item_name || c.name, orig: null, cur: c })
    }
  })
  return rows
}

const Approvals = () => {
  const classes = useStyles()
  const [orders, setOrders] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [rejectTarget, setRejectTarget] = React.useState(null)
  const [rejectRemarks, setRejectRemarks] = React.useState('')
  const [viewOrder, setViewOrder] = React.useState(null)
  // editable per-line fulfilment state for the order-detail screen
  const [procLines, setProcLines] = React.useState([])
  const [savingLines, setSavingLines] = React.useState(false)
  // order-level bulk selection (submitted orders only)
  const [selected, setSelected] = React.useState([])
  const [bulkReject, setBulkReject] = React.useState(false)
  const [bulkRejectRemarks, setBulkRejectRemarks] = React.useState('')
  // Row overflow (⋮) menu: housekeeping actions live here to declutter the row.
  const [menuAnchor, setMenuAnchor] = React.useState(null)
  const [menuOrder, setMenuOrder] = React.useState(null)
  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuOrder(null)
  }

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchAllOrders()
      setOrders(res?.results || [])
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load orders',
        constants.ERROR,
        'Approvals'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const decide = async (o, decision, remarks = '') => {
    try {
      await decideOrder(o.centre, o.month, decision, remarks)
      notify(`Order ${decision}`, '', 'success')
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Action failed', constants.ERROR, 'Approvals')
    }
  }

  // P3: mark an approved order sent directly from the list.
  const markSent = async (o) => {
    try {
      await setOrderLineStatuses(o.centre, o.month, [], true)
      notify('Order marked sent', '', 'success')
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Action failed', constants.ERROR, 'Approvals')
    }
  }

  // P4: HQ delete an order, or reopen it for the centre to edit.
  const adminAction = async (o, action) => {
    try {
      await orderAdminAction(o.centre, o.month, action)
      notify(action === 'delete' ? 'Order deleted' : 'Order reopened for editing', '', 'success')
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Action failed', constants.ERROR, 'Approvals')
    }
  }
  const confirmDelete = (o) =>
    confirm(
      () => adminAction(o, 'delete'),
      `Delete order ${o.po_number || ''} (${o.centreName})? This cannot be undone.`,
      () => {}
    )

  const openView = async (o) => {
    try {
      const res = await fetchProgramMaterial({ centre: o.centre, month: o.month })
      const lines = res?.lines || []
      setViewOrder({
        ...o,
        status: res?.status || o.status,
        discrepancy: res?.discrepancy ?? o.discrepancy,
        discrepancy_notes: res?.discrepancy_notes ?? o.discrepancy_notes,
        centre_feedback: res?.centre_feedback || o.centre_feedback || '',
        original_snapshot: res?.original_snapshot || [],
        lines
      })
      setProcLines(
        lines.map((l) => ({
          uuid: l.uuid,
          item_name: l.item_name,
          reference_id: l.reference_id,
          reference_link: l.reference_link,
          reference_image_url: l.reference_image_url,
          qty: l.qty,
          unit_cost: l.unit_cost,
          line_total: l.line_total,
          remarks: l.remarks,
          line_status: l.line_status || 'pending',
          line_note: l.line_note || '',
          line_decision: l.line_decision || 'pending',
          decision_note: l.decision_note || '',
          receipt_status: l.receipt_status || 'pending',
          receipt_note: l.receipt_note || ''
        }))
      )
    } catch (error) {
      eventAlert(
        error?.message || 'Could not load order',
        constants.ERROR,
        'Approvals'
      )
    }
  }

  const updateProcLine = (uuid, patch) =>
    setProcLines((prev) =>
      prev.map((l) => (l.uuid === uuid ? { ...l, ...patch } : l))
    )

  // Fulfilment (sent / not-available) is editable once the order is approved.
  const lineEditable = viewOrder && viewOrder.status === 'approved'
  // Partial-approval decisions (accept/reject) are set while still submitted.
  const decisionEditable = viewOrder && viewOrder.status === 'submitted'

  const saveDecisions = async (thenApprove = false) => {
    if (!viewOrder) return
    const payload = procLines.map((l) => ({
      uuid: l.uuid,
      line_decision: l.line_decision,
      decision_note: l.decision_note || ''
    }))
    setSavingLines(true)
    try {
      await setOrderLineDecisions(viewOrder.centre, viewOrder.month, {
        lines: payload
      })
      if (thenApprove) {
        await decideOrder(viewOrder.centre, viewOrder.month, 'approved')
        notify('Order approved', '', 'success')
      } else {
        notify('Line decisions saved', '', 'success')
      }
      setViewOrder(null)
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Action failed', constants.ERROR, 'Approvals')
    } finally {
      setSavingLines(false)
    }
  }

  const bulkDecision = (value) =>
    setProcLines((prev) => prev.map((l) => ({ ...l, line_decision: value })))

  const saveLines = async (markSent = false) => {
    if (!viewOrder) return
    const payload = procLines.map((l) => ({
      uuid: l.uuid,
      line_status: l.line_status,
      line_note: l.line_note || ''
    }))
    setSavingLines(true)
    try {
      await setOrderLineStatuses(
        viewOrder.centre,
        viewOrder.month,
        payload,
        markSent
      )
      notify(markSent ? 'Order marked sent' : 'Line statuses saved', '', 'success')
      setViewOrder(null)
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Action failed', constants.ERROR, 'Approvals')
    } finally {
      setSavingLines(false)
    }
  }

  const confirmReject = async () => {
    const target = rejectTarget
    setRejectTarget(null)
    await decide(target, 'rejected', rejectRemarks)
    setRejectRemarks('')
  }

  // ---- order-level bulk actions ----
  // Submitted -> approve/reject. Approved-and-later (have a PO) -> download POs.
  const DOWNLOADABLE = ['approved', 'sent', 'received', 'fulfilled']
  const submittedOrders = orders.filter((o) => o.status === 'submitted')
  // Rows you can tick: ones you can act on (approve/reject or download).
  const selectableOrders = orders.filter(
    (o) => o.status === 'submitted' || DOWNLOADABLE.includes(o.status)
  )
  const keyOf = (o) => `${o.centre}|${o.month}`
  const isSelected = (o) => selected.includes(keyOf(o))
  const isSelectable = (o) =>
    o.status === 'submitted' || DOWNLOADABLE.includes(o.status)
  const toggleSelect = (o) =>
    setSelected((prev) =>
      prev.includes(keyOf(o))
        ? prev.filter((k) => k !== keyOf(o))
        : [...prev, keyOf(o)]
    )
  const toggleSelectAll = () =>
    setSelected((prev) =>
      prev.length === selectableOrders.length
        ? []
        : selectableOrders.map(keyOf)
    )
  const selectedOrders = () =>
    orders.filter((o) => selected.includes(keyOf(o)) && o.status === 'submitted')
  const selectedForDownload = () =>
    orders.filter(
      (o) => selected.includes(keyOf(o)) && DOWNLOADABLE.includes(o.status)
    )

  const bulkDownloadPos = async () => {
    const targets = selectedForDownload()
    if (isEmpty(targets)) return
    try {
      const n = await downloadProgramMaterialPos(targets)
      notify(
        n > 1 ? `Downloaded ${n} POs (ZIP)` : 'PO downloaded',
        '',
        'success'
      )
    } catch (error) {
      eventAlert(
        error?.message || 'Could not generate the POs',
        constants.ERROR,
        'Approvals'
      )
    }
  }

  const bulkApprove = async () => {
    const targets = selectedOrders()
    if (isEmpty(targets)) return
    try {
      for (const o of targets) {
        await decideOrder(o.centre, o.month, 'approved')
      }
      notify(`${targets.length} order(s) approved`, '', 'success')
      setSelected([])
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Bulk approve failed', constants.ERROR, 'Approvals')
    }
  }

  const confirmBulkReject = async () => {
    const targets = selectedOrders()
    setBulkReject(false)
    try {
      for (const o of targets) {
        await decideOrder(o.centre, o.month, 'rejected', bulkRejectRemarks)
      }
      notify(`${targets.length} order(s) rejected`, '', 'success')
      setSelected([])
      setBulkRejectRemarks('')
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Bulk reject failed', constants.ERROR, 'Approvals')
    }
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <Box
        display="flex"
        alignItems="center"
        mb={2}
        style={{ gap: 12, flexWrap: 'wrap' }}>
        <Typography variant="h6">Pending Orders (HQ) — Program Material</Typography>
        <div style={{ flexGrow: 1 }} />
        {selected.length > 0 && (
          <>
            <Typography variant="body2" color="textSecondary">
              {selected.length} selected
            </Typography>
            {selectedOrders().length > 0 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  style={{ background: '#2e7d32', color: '#fff' }}
                  onClick={bulkApprove}>
                  Approve selected
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  style={{ color: '#c62828', borderColor: '#c62828' }}
                  onClick={() => {
                    setBulkRejectRemarks('')
                    setBulkReject(true)
                  }}>
                  Reject selected
                </Button>
              </>
            )}
            {selectedForDownload().length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={bulkDownloadPos}>
                Download POs ({selectedForDownload().length})
              </Button>
            )}
          </>
        )}
        <ExportButtons
          getData={() => ({
            filename: 'pending-orders',
            title: 'Program Material Orders',
            headers: ['PO', 'Centre', 'Month', 'Items', 'Total', 'Status'],
            rows: orders.map((o) => [
              o.po_number || '',
              o.centreName,
              Dayjs(o.month).format('MMMM YYYY'),
              o.items,
              Number(o.total || 0),
              o.status
            ])
          })}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={isEmpty(selectableOrders)}
                  checked={
                    !isEmpty(selectableOrders) &&
                    selected.length === selectableOrders.length
                  }
                  indeterminate={
                    selected.length > 0 &&
                    selected.length < selectableOrders.length
                  }
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>PO</TableCell>
              <TableCell>Centre</TableCell>
              <TableCell>Month</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Files</TableCell>
              <TableCell align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={`${o.centre}|${o.month}`} hover>
                <TableCell padding="checkbox">
                  {isSelectable(o) ? (
                    <Checkbox
                      size="small"
                      checked={isSelected(o)}
                      onChange={() => toggleSelect(o)}
                    />
                  ) : null}
                </TableCell>
                <TableCell>{o.po_number || '—'}</TableCell>
                <TableCell>{o.centreName}</TableCell>
                <TableCell>{Dayjs(o.month).format('MMMM YYYY')}</TableCell>
                <TableCell align="right">{o.items}</TableCell>
                <TableCell align="right">{inr(o.total)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STATUS_LABEL[o.status] || o.status}
                    style={STATUS_COLOR[o.status]}
                  />
                  {o.status === 'rejected' && o.review_remarks ? (
                    <div style={{ fontSize: 11, color: '#c62828' }}>
                      {o.review_remarks}
                    </div>
                  ) : null}
                  {o.status === 'received' && o.discrepancy ? (
                    <div style={{ fontSize: 11, color: '#c62828' }}>
                      ⚠ issues{o.discrepancy_notes ? `: ${o.discrepancy_notes}` : ''}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell align="center">{o.attachments || 0}</TableCell>
                <TableCell align="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 4, flexWrap: 'nowrap', width: '100%' }}>
                    {/* Primary, status-relevant actions left-align so they line
                        up across rows; the ⋮ menu pins to the right edge. */}
                    <Button size="small" onClick={() => openView(o)}>
                      {o.status === 'approved' ? 'Process' : 'View'}
                    </Button>
                    {o.status === 'submitted' && (
                      <>
                        <Button
                          size="small"
                          style={{ color: '#2e7d32' }}
                          onClick={() => decide(o, 'approved')}>
                          Approve
                        </Button>
                        <Button
                          size="small"
                          style={{ color: '#c62828' }}
                          onClick={() => {
                            setRejectRemarks('')
                            setRejectTarget(o)
                          }}>
                          Reject
                        </Button>
                      </>
                    )}
                    {o.status === 'approved' && (
                      <Button
                        size="small"
                        style={{ color: '#5e35b1' }}
                        onClick={() => markSent(o)}>
                        Mark Sent
                      </Button>
                    )}
                    {/* Housekeeping (Allow edit / Delete) tucked into a ⋮ menu. */}
                    <IconButton
                      size="small"
                      style={{ marginLeft: 'auto' }}
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget)
                        setMenuOrder(o)
                      }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {isEmpty(orders) && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="textSecondary">
                    No submitted orders yet. Submit a Program Material order
                    first.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {menuOrder && menuOrder.status !== 'draft' && (
          <MenuItem
            onClick={() => {
              const o = menuOrder
              closeMenu()
              adminAction(o, 'reopen')
            }}>
            Allow edit (reopen)
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            const o = menuOrder
            closeMenu()
            confirmDelete(o)
          }}
          style={{ color: '#c62828' }}>
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)}>
        <DialogTitle>Reject order</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Reason / remarks"
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={confirmReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewOrder}
        onClose={() => setViewOrder(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          {viewOrder?.po_number || 'Order'} — {viewOrder?.centreName} ·{' '}
          {viewOrder ? Dayjs(viewOrder.month).format('MMMM YYYY') : ''}
        </DialogTitle>
        <DialogContent>
          <Box mb={1} display="flex" justifyContent="flex-end">
            <ExportButtons
              getData={() => ({
                filename: `order-${viewOrder?.centreName}-${
                  viewOrder ? Dayjs(viewOrder.month).format('YYYY-MM') : ''
                }`,
                title: `${viewOrder?.po_number || 'Order'} — ${
                  viewOrder?.centreName
                }`,
                headers: [
                  'Sl No.',
                  'Item',
                  'Reference ID',
                  'Qty',
                  'Unit Cost',
                  'Line Total',
                  'Remarks'
                ],
                rows: (viewOrder?.lines || []).map((l, i) => [
                  i + 1,
                  l.item_name,
                  l.reference_id || '',
                  Number(l.qty),
                  Number(l.unit_cost),
                  Number(
                    l.line_total != null
                      ? l.line_total
                      : Number(l.qty) * Number(l.unit_cost)
                  ),
                  l.remarks || ''
                ])
              })}
            />
          </Box>
          {viewOrder?.status === 'received' && viewOrder?.discrepancy ? (
            <Box
              mb={2}
              p={1.5}
              style={{ background: '#ffebee', borderRadius: 8, color: '#c62828' }}>
              <strong>Issues reported by centre:</strong>{' '}
              {viewOrder.discrepancy_notes || '—'}
            </Box>
          ) : null}
          {viewOrder?.centre_feedback ? (
            <Box
              mb={2}
              p={1.5}
              style={{ background: '#e8eaf6', borderRadius: 8, color: '#283593' }}>
              <strong>Centre remark:</strong> {viewOrder.centre_feedback}
            </Box>
          ) : null}
          {lineEditable ? (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Set each line&apos;s fulfilment, then <strong>Save</strong>. Use{' '}
              <strong>Mark order Sent</strong> once an approved order has been
              dispatched.
            </Typography>
          ) : null}
          {decisionEditable ? (
            <Box mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="body2" color="textSecondary">
                Accept or reject each line, then approve. Rejected lines drop out
                of the order.
              </Typography>
              <div style={{ flexGrow: 1 }} />
              <Button size="small" onClick={() => bulkDecision('accepted')}>
                Accept all
              </Button>
              <Button
                size="small"
                style={{ color: '#c62828' }}
                onClick={() => bulkDecision('rejected')}>
                Reject all
              </Button>
            </Box>
          ) : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sl No.</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Reference ID</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost (₹)</TableCell>
                <TableCell align="right">Line Total (₹)</TableCell>
                <TableCell>Decision</TableCell>
                <TableCell>Fulfilment</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {procLines.map((l, i) => (
                <TableRow key={l.uuid || i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    {l.item_name}
                    {l.reference_link ? (
                      <a
                        href={l.reference_link}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 12 }}>
                        view ↗
                      </a>
                    ) : null}
                    {l.reference_image_url ? (
                      <a
                        href={l.reference_image_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 12 }}>
                        image ↗
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell>{l.reference_id || '—'}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{l.unit_cost}</TableCell>
                  <TableCell align="right">
                    {Number(
                      l.line_total != null
                        ? l.line_total
                        : Number(l.qty) * Number(l.unit_cost)
                    ).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell>
                    {decisionEditable ? (
                      <>
                        <TextField
                          select
                          size="small"
                          variant="outlined"
                          style={{ minWidth: 110 }}
                          value={l.line_decision}
                          onChange={(e) =>
                            updateProcLine(l.uuid, {
                              line_decision: e.target.value
                            })
                          }>
                          {LINE_DECISIONS.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="note (optional)"
                          style={{ minWidth: 140, marginTop: 6 }}
                          value={l.decision_note}
                          onChange={(e) =>
                            updateProcLine(l.uuid, {
                              decision_note: e.target.value
                            })
                          }
                        />
                      </>
                    ) : l.line_decision && l.line_decision !== 'pending' ? (
                      <div>
                        <Chip
                          size="small"
                          label={l.line_decision === 'accepted' ? 'Accepted' : 'Rejected'}
                          style={{
                            ...(l.line_decision === 'accepted'
                              ? STATUS_COLOR.approved
                              : STATUS_COLOR.rejected)
                          }}
                        />
                        {l.decision_note ? (
                          <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                            {l.decision_note}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {lineEditable ? (
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        style={{ minWidth: 130 }}
                        value={l.line_status}
                        onChange={(e) =>
                          updateProcLine(l.uuid, { line_status: e.target.value })
                        }>
                        {LINE_STATUSES.map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Chip
                        size="small"
                        label={
                          LINE_STATUSES.find((s) => s.value === l.line_status)
                            ?.label || l.line_status
                        }
                        style={{
                          ...(l.line_status === 'sent'
                            ? STATUS_COLOR.approved
                            : l.line_status === 'unavailable'
                            ? STATUS_COLOR.rejected
                            : {}),
                          textTransform: 'lowercase'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {lineEditable ? (
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="note"
                        style={{ width: 160 }}
                        value={l.line_note}
                        onChange={(e) =>
                          updateProcLine(l.uuid, { line_note: e.target.value })
                        }
                      />
                    ) : (
                      l.line_note || '—'
                    )}
                    {/* Centre's receipt issue, for HQ to resolve. */}
                    {l.receipt_status === 'not_received' ? (
                      <Box mt={1}>
                        <Typography variant="caption" style={{ color: '#c62828' }}>
                          Not received by centre
                        </Typography>
                      </Box>
                    ) : null}
                    {l.receipt_status === 'discrepancy' ? (
                      <Box mt={1}>
                        <Typography variant="caption" style={{ color: '#c62828' }}>
                          Discrepancy: {l.receipt_note || '—'}
                        </Typography>
                        <AttachmentUploader
                          entityType="pm_line"
                          line={l.uuid}
                          label="Centre's photo"
                          disabled
                        />
                      </Box>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {isEmpty(procLines) && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="textSecondary">
                      No lines.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Original vs current — what the centre changed since first submitting */}
          {viewOrder && !isEmpty(viewOrder.original_snapshot) ? (
            <OriginalVsCurrent
              original={viewOrder.original_snapshot}
              current={viewOrder.lines}
            />
          ) : null}

          {/* P5: proof of payment / dispatch — attach when marking the order Sent */}
          {viewOrder ? (
            <Box mt={2}>
              <AttachmentUploader
                entityType="pm"
                centre={viewOrder.centre}
                month={viewOrder.month}
                label="Proof of payment / dispatch"
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOrder(null)}>Close</Button>
          {decisionEditable ? (
            <>
              <Button
                disabled={savingLines}
                onClick={() => saveDecisions(false)}>
                Save decisions
              </Button>
              <Button
                style={{ color: '#c62828' }}
                disabled={savingLines}
                onClick={() => {
                  setRejectRemarks('')
                  setRejectTarget(viewOrder)
                  setViewOrder(null)
                }}>
                Reject order
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={savingLines}
                onClick={() => saveDecisions(true)}>
                {savingLines ? 'Saving…' : 'Approve'}
              </Button>
            </>
          ) : null}
          {lineEditable ? (
            <Button
              color="primary"
              disabled={savingLines}
              onClick={() => saveLines(false)}>
              {savingLines ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
          {viewOrder?.status === 'approved' ? (
            <Button
              variant="contained"
              color="primary"
              disabled={savingLines}
              onClick={() => saveLines(true)}>
              Mark order Sent
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={bulkReject} onClose={() => setBulkReject(false)}>
        <DialogTitle>Reject {selected.length} order(s)</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Reason / remarks (applied to all)"
            value={bulkRejectRemarks}
            onChange={(e) => setBulkRejectRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReject(false)}>Cancel</Button>
          <Button color="secondary" variant="contained" onClick={confirmBulkReject}>
            Reject selected
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

// Collapsible "original PO vs current" comparison for HQ.
const OriginalVsCurrent = ({ original, current }) => {
  const [open, setOpen] = React.useState(false)
  const rows = React.useMemo(
    () => diffLines(original, current).filter((r) => r.kind !== 'same'),
    [original, current]
  )
  const TONE = {
    added: { background: '#e8f5e9', label: 'Added' },
    removed: { background: '#ffebee', label: 'Removed' },
    changed: { background: '#fff8e1', label: 'Changed' }
  }
  return (
    <Box mt={2}>
      <Button size="small" onClick={() => setOpen((v) => !v)}>
        {open ? '▾' : '▸'} Original vs current PO
        {rows.length ? ` (${rows.length} change${rows.length > 1 ? 's' : ''})` : ' (no changes)'}
      </Button>
      {open && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Change</TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="right">Original qty × cost</TableCell>
              <TableCell align="right">Current qty × cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty(rows) ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No changes since the order was first submitted.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.key} style={{ background: TONE[r.kind].background }}>
                  <TableCell>{TONE[r.kind].label}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">
                    {r.orig
                      ? `${numv(r.orig.qty)} × ${numv(r.orig.unit_cost)}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.cur
                      ? `${numv(r.cur.qty)} × ${numv(r.cur.unit_cost)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default Approvals
